import { PostBlockedHoursI } from '@/types/cyclone/requests';
import React, { FunctionComponent, useEffect } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { BlockHours } from './BlockHours';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAuth } from '@/contexts';
import { toast } from 'react-toastify';

dayjs.extend(utc);
dayjs.extend(timezone);

type BlockHoursModalProps = {
  show: boolean;
  onClose: () => void;
  onSave: (data: PostBlockedHoursI, isEdit: boolean, editId?: number) => void;
  editData?: any;
  isEditMode?: boolean;
};

export type BlockHoursFormState = {
  note?: string;
  startAt: Date;
  endAt: Date;
  startTime: string;
  endTime: string;
};

const FORMAT_STRING = 'YYYY-MM-DDTHH:mm:ss';

export const BlockHoursModal: FunctionComponent<BlockHoursModalProps> = ({
  show,
  onClose,
  onSave,
  editData,
  isEditMode = false
}) => {
  const { control, handleSubmit, watch, setValue, reset } = useForm<BlockHoursFormState>({
    defaultValues: {
      note: '',
      startAt: new Date(),
      endAt: new Date(),
      startTime: '',
      endTime: ''
    }
  });

  useEffect(() => {
    if (isEditMode && editData && show) {
      setTimeout(() => {
        const startDate = dayjs(editData.start_at).toDate();
        const endDate = dayjs(editData.end_at).toDate();

        const startTime = editData.start_time || '';
        const endTime = editData.end_time || '';

        let formattedStartTime = '';
        let formattedEndTime = '';

        if (startTime && startTime.length >= 5) {
          formattedStartTime = startTime.substring(0, 5); // Asegurar formato HH:mm
        } else {
          formattedStartTime = dayjs(editData.start_at).format('HH:mm');
        }

        if (endTime && endTime.length >= 5) {
          formattedEndTime = endTime.substring(0, 5); // Asegurar formato HH:mm
        } else {
          formattedEndTime = dayjs(editData.end_at).format('HH:mm');
        }

        const formValues = {
          note: editData.note || editData.notes || editData.description || '',
          startAt: startDate,
          endAt: endDate,
          startTime: formattedStartTime,
          endTime: formattedEndTime
        };

        reset(formValues);
      }, 0);
    }
  }, [isEditMode, editData, show, reset]);

  useEffect(() => {
    if (!isEditMode && show) {
      setTimeout(() => {
        const now = new Date();
        const currentTime = dayjs(now).format('HH:mm');
        const oneHourLater = dayjs(now).add(1, 'hour').format('HH:mm');

        const defaultValues = {
          note: '',
          startAt: now,
          endAt: now,
          startTime: currentTime,
          endTime: oneHourLater
        };

        reset(defaultValues);
      }, 0);
    }
  }, [isEditMode, show, reset]);
  const { session } = useAuth();

  const timezone = session?.vendor?.timezone || 'America/Argentina/Buenos_Aires';

  const [dateChange, setDateChange] = React.useState(false);
  const [timeChange, setTimeChange] = React.useState(false);

  const startAt = watch('startAt');
  const endAt = watch('endAt');
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  React.useEffect(() => {
    if (dateChange) {
      setTimeout(() => {
        setDateChange(false);
      }, 1500);
    }
    if (timeChange) {
      setTimeout(() => {
        setTimeChange(false);
      }, 1500);
    }
  }, [dateChange, timeChange]);

  React.useEffect(() => {
    if (isEditMode) return;
    if (startAt && endAt && startTime && endTime) {
      const combinedStart = dayjs.tz(`${dayjs(startAt).format('YYYY-MM-DD')}T${startTime}`, timezone);
      const combinedEnd = dayjs.tz(`${dayjs(endAt).format('YYYY-MM-DD')}T${endTime}`, timezone);

      const startDate = dayjs(startAt).startOf('day');
      const endDate = dayjs(endAt).startOf('day');

      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      if (startDate.isAfter(endDate)) {
        if (startHour < endHour || (startHour === endHour && startMinute < endMinute)) {
          setValue('endAt', dayjs(startAt).toDate());
          toast.info(
            'La fecha de finalización era anterior a la de inicio. Se ajustó al mismo día que la fecha de inicio.',
            { style: { backgroundColor: '#0072FB', color: '#FFF' } }
          );
        } else {
          setValue('endAt', dayjs(startAt).add(1, 'day').toDate());
          toast.info('La fecha de finalización era anterior a la de inicio. Se ajustó al día siguiente.', {
            style: { backgroundColor: '#0072FB', color: '#FFF' }
          });
        }
        setDateChange(true);
      } else if (combinedStart.isSame(combinedEnd) && startTime === endTime) {
        setValue('endTime', combinedEnd.add(30, 'minutes').format('HH:mm'));
        toast.info(
          'El tiempo de finalización era igual al tiempo de inicio. Se añadieron 30 minutos para evitar conflictos.',
          { style: { backgroundColor: '#0072FB', color: '#FFF' } }
        );
        setTimeChange(true);
      } else if (combinedEnd.isBefore(combinedStart)) {
        if (startDate.isSame(endDate) && startHour > endHour) {
          setValue('endAt', dayjs(startAt).add(1, 'day').toDate());
          toast.info(
            'El tiempo de finalización ocurría antes del tiempo de inicio. Se ajustó al día siguiente.',
            { style: { backgroundColor: '#0072FB', color: '#FFF' } }
          );
          setDateChange(true);
        }
      }
    }
  }, [endAt, endTime, startAt, startTime, timezone, setValue, isEditMode]);

  const fullSubmit = () => {
    onClose();
    handleSubmit(onSubmit)();
  };
  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const combinedStart = dayjs
      .tz(`${dayjs(data.startAt).format('YYYY-MM-DD')}T${data.startTime}`, timezone)
      .format(FORMAT_STRING);
    const combinedEnd = dayjs
      .tz(`${dayjs(data.endAt).format('YYYY-MM-DD')}T${data.endTime}`, timezone)
      .format(FORMAT_STRING);

    onSave(
      {
        note: data.note,
        start_at: combinedStart,
        end_at: combinedEnd,
        start_time: data.startTime,
        end_time: data.endTime
      },
      isEditMode,
      editData?.id
    );
  };

  return (
    <BlockHours
      show={show}
      onClose={onClose}
      control={control}
      onSubmit={fullSubmit}
      startAt={startAt}
      dateChange={dateChange}
      timeChange={timeChange}
      isEditMode={isEditMode}
    />
  );
};
